import { useLayoutEffect, useRef } from 'react'

export interface ScorePop {
  id: number
  /** A catch flies from its ring into the scoreboard; a loss falls from where its ring hung. */
  kind: 'gain' | 'loss' | 'star'
  /** What it says: +1, or −1 for a ring that has fallen. */
  label: string
  /** Where the ring hangs (or, for a loss, hung), in CSS pixels. */
  x: number
  y: number
  /** The ring's colour: the +1 is the ring's own. */
  color: string
  /** Waits this long before it starts, so a handful of catches go off one after another. */
  delay: number
}

/** From the ring to the scoreboard: pop up, hang a beat, fly into it. */
export const POP_FLIGHT_MS = 1100
/** A loss falling away from where its ring hung. */
const LOSS_MS = 1000

const Pop: React.FC<{ pop: ScorePop; onDone: (id: number) => void }> = ({ pop, onDone }) => {
  const ref = useRef<HTMLSpanElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const at = (x: number, y: number, scale: number) =>
      `translate(${x}px, ${y}px) translate(-50%, -50%) scale(${scale})`
    // The scoreboard, if it is up: the +1 lands in it. If not, it just floats up and fades.
    const pill = document.querySelector('[data-testid="score"]')?.getBoundingClientRect()
    const to = pill ? { x: pill.left + pill.width / 2, y: pill.top + pill.height / 2 } : { x: pop.x, y: pop.y - 90 }
    // A loss falls with its ring: from where it hung, a −1 in the ring's colour sinks and fades.
    const animation =
      pop.kind === 'loss'
        ? el.animate(
            [
              { transform: at(pop.x, pop.y, 0.6), opacity: 0 },
              { transform: at(pop.x, pop.y - 6, 1.1), opacity: 0.9, offset: 0.18, easing: 'ease-in' },
              { transform: at(pop.x, pop.y + 70, 0.85), opacity: 0 },
            ],
            { duration: LOSS_MS, delay: pop.delay, fill: 'both' }
          )
        : el.animate(
            [
              { transform: at(pop.x, pop.y, 0.4), opacity: 0 },
              { transform: at(pop.x, pop.y - 34, 1.2), opacity: 1, offset: 0.2, easing: 'ease-out' },
              {
                transform: at(pop.x, pop.y - 44, 1),
                opacity: 1,
                offset: 0.42,
                easing: 'cubic-bezier(0.55, 0, 0.8, 0.2)',
              },
              { transform: at(to.x, to.y, 0.5), opacity: 0.9, offset: 0.97 },
              { transform: at(to.x, to.y, 0.4), opacity: 0 },
            ],
            { duration: POP_FLIGHT_MS, delay: pop.delay, fill: 'both' }
          )
    animation.onfinish = () => onDone(pop.id)
    return () => animation.cancel()
  }, [pop, onDone])
  return (
    <span
      ref={ref}
      className={`absolute top-0 left-0 font-bold tabular-nums ${
        pop.kind === 'loss'
          ? 'text-xl md:text-2xl'
          : pop.kind === 'star'
            ? 'star-shine text-5xl md:text-6xl'
            : 'text-2xl md:text-3xl'
      }`}
      style={
        pop.kind === 'star'
          ? undefined
          : pop.kind === 'loss'
            ? { color: pop.color, textShadow: '0 0 2px #000' }
            : { color: pop.color, textShadow: `0 0 12px ${pop.color}, 0 0 2px #000` }
      }
    >
      {pop.label}
    </span>
  )
}

/**
 * A +1 for every ring that has just started to count, rising from where it hangs in the ring's
 * colour and flying into the scoreboard, which takes the new number as they land; a −1 for
 * every ring that has fallen, sinking away from where it hung, dimmer and without the glow; and
 * a big silver star when the star ring is caught. Purely for the eye: the scoreboard says the
 * score to assistive tech.
 */
export const ScorePops: React.FC<{ pops: ScorePop[]; onDone: (id: number) => void }> = ({ pops, onDone }) => (
  <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[26] overflow-hidden">
    {pops.map((pop) => (
      <Pop key={pop.id} pop={pop} onDone={onDone} />
    ))}
  </div>
)
